import { useEffect, useState } from "react";
import { api, imgSrc } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, AdminField } from "./shared";

const EMPTY_EXP = { name: "", category: "Coffee", description: "", duration: "60 min", price: 100, image: "", active: true };

export default function ExperiencesTab() {
    const { t } = useT();
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const refresh = () => api.get("/experiences?active=").then((r) => setItems(r.data));
    useEffect(() => { refresh(); }, []);

    const save = async (data) => {
        try {
            const payload = { ...data, price: Number(data.price) };
            if (editing.id) await api.put(`/experiences/${editing.id}`, payload);
            else await api.post("/experiences", payload);
            toast.success("Experience saved"); setEditing(null); refresh();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this experience?")) return;
        await api.delete(`/experiences/${id}`); refresh(); toast.success("Deleted");
    };

    return (
        <div data-testid="admin-experiences">
            <PageHeader
                eyebrow="Admin · Experiences"
                title="Curated rituals."
                action={<button onClick={() => setEditing({ ...EMPTY_EXP })} data-testid="exp-new" className="inline-flex items-center gap-2 px-5 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold"><Plus size={14} /> New experience</button>}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {items.map((e) => (
                    <div key={e.id} className="border border-espresso/15 p-5 flex gap-5">
                        <div className="w-32 h-32 bg-bone2 overflow-hidden flex-shrink-0"><img src={imgSrc(e.image)} alt={e.name} className="w-full h-full object-cover" /></div>
                        <div className="flex-1">
                            <div className="eyebrow mb-2">{e.category} · {e.duration} · ${e.price}</div>
                            <h3 className="font-serif text-2xl mb-2">{e.name}</h3>
                            <p className="text-sm text-muted2 line-clamp-2 mb-3">{e.description}</p>
                            <div className="flex gap-3">
                                <button onClick={() => setEditing(e)} data-testid={`exp-edit-${e.id}`} className="text-xs uppercase tracking-widest2 text-forest hover:text-terracotta">Edit</button>
                                <button onClick={() => remove(e.id)} data-testid={`exp-delete-${e.id}`} className="text-xs uppercase tracking-widest2 text-terracotta hover:text-forest">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {editing && (
                <Dialog open onOpenChange={() => setEditing(null)}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-3xl">{t("admin.experienceDialogTitle", !!editing.id)}</DialogTitle>
                            <DialogDescription>{t("admin.experienceDialogDesc")}</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <AdminField label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="exp-form-name" />
                            <AdminField label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} testid="exp-form-category" />
                            <AdminField label="Duration" value={editing.duration} onChange={(v) => setEditing({ ...editing, duration: v })} testid="exp-form-duration" />
                            <AdminField label="Price" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testid="exp-form-price" />
                            <ExperienceImageUploader value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
                            <div>
                                <label className="eyebrow block mb-2">Description</label>
                                <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="exp-form-desc" className="w-full border border-espresso/20 p-3 text-sm bg-transparent" />
                            </div>
                            <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
                        </div>
                        <DialogFooter className="mt-6">
                            <button onClick={() => setEditing(null)} className="px-5 py-3 border border-espresso/30 text-xs uppercase tracking-widest2">Cancel</button>
                            <button onClick={() => save(editing)} data-testid="exp-form-save" className="px-5 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold">Save</button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

function ExperienceImageUploader({ value, onChange }) {
    const [uploading, setUploading] = useState(false);

    const onPickFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post("/admin/uploads/experience-image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(res.data.url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div data-testid="exp-image-uploader">
            <label className="eyebrow block mb-2">Image</label>
            <div className="flex items-center gap-4">
                <div className="w-28 h-28 bg-bone2 border border-espresso/15 overflow-hidden flex-shrink-0 relative">
                    {value ? (
                        <>
                            <img src={imgSrc(value)} alt="Experience" className="w-full h-full object-cover" data-testid="exp-image-preview" />
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                data-testid="exp-image-remove"
                                className="absolute top-1 right-1 bg-espresso/80 text-bone p-1 hover:bg-terracotta transition-colors"
                            >
                                <X size={12} />
                            </button>
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-muted2 uppercase tracking-widest2">No image</div>
                    )}
                </div>
                <label className="border border-dashed border-espresso/30 bg-bone2/40 px-5 py-4 flex flex-col items-center justify-center cursor-pointer hover:bg-bone2 transition-colors text-xs uppercase tracking-widest2 text-espresso" data-testid="exp-image-dropzone">
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/avif" onChange={onPickFile} className="hidden" data-testid="exp-image-file-input" disabled={uploading} />
                    <Upload size={18} className="mb-2 text-forest" />
                    <span>{uploading ? "Uploading…" : value ? "Replace" : "Upload"}</span>
                    <span className="text-[10px] text-muted2 mt-1 normal-case tracking-normal">PNG · JPG · WEBP · up to 8 MB</span>
                </label>
            </div>
            <p className="text-[11px] text-muted2 mt-2">Upload an image from your device.</p>
        </div>
    );
}
